import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import PrinterFilamentSlots from '../../components/printers/PrinterFilamentSlots';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import MaintenanceOverviewCards from '../../components/printer-maintenances/MaintenanceOverviewCards';
import MaintenancePrinterSummaryCard from '../../components/printer-maintenances/MaintenancePrinterSummaryCard';
import UpcomingMaintenancesPanel from '../../components/printer-maintenances/UpcomingMaintenancesPanel';
import CustomPagination from '../../components/Pagination';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import ViewModeToggle from '../../components/ViewModeToggle';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate, presentUuid } from '../../helpers/formatters';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import { sortPrintersByName } from '../../helpers/printers';
import { formatMaintenanceDurationLiteral } from '../../helpers/printersOverview';
import {
    MAINTENANCES_PRINTER_SUMMARY_VIEW_KEY,
    readViewMode,
} from '../../helpers/viewModePreference';
import {
    buildMaintenanceOverviewStatsFromSummary,
    buildMaintenanceSummaryCardsFromAggregates,
    buildPrinterMaintenanceFilterQuery,
    buildPrinterMaintenanceListQuery,
    formatMaintenancePeriodLabel,
    formatReplacementPartNames,
    getMaintenanceTypeBadgeProps,
    MAINTENANCE_TYPES,
    maintenancePartsTotalCents,
    minutesToDuration,
    replacementPartsCountLabel,
} from '../../helpers/printerMaintenances';

function ListPrinterMaintenancesPage() {
    const [searchParams] = useSearchParams();
    const initialPrinterUuid = searchParams.get('printer_uuid') || '';
    const [maintenances, setMaintenances] = useState([]);
    const [summaryPayload, setSummaryPayload] = useState(null);
    const [printers, setPrinters] = useState([]);
    const [printersById, setPrintersById] = useState({});
    const [draftPrinter, setDraftPrinter] = useState(initialPrinterUuid);
    const [draftType, setDraftType] = useState('');
    const [draftOverviewFrom, setDraftOverviewFrom] = useState('');
    const [draftOverviewTo, setDraftOverviewTo] = useState('');
    const [queryFilters, setQueryFilters] = useState({
        printer_uuid: initialPrinterUuid,
        type: '',
        occurred_from: '',
        occurred_to: '',
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [listVersion, setListVersion] = useState(0);
    const [summaryViewMode, setSummaryViewMode] = useState(
        () => readViewMode(MAINTENANCES_PRINTER_SUMMARY_VIEW_KEY),
    );
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const hasActiveFilters = Boolean(
        queryFilters.printer_uuid
        || queryFilters.type
        || queryFilters.occurred_from
        || queryFilters.occurred_to,
    );

    useEffect(() => {
        api.get('printers?page=1&limit=100')
            .then((response) => {
                const list = sortPrintersByName(response.data.printers || []);
                setPrinters(list);
                const byId = {};
                list.forEach((printer) => {
                    byId[printer.id] = printer.name;
                });
                setPrintersById(byId);
            })
            .catch(() => {});
    }, []);

    const fetchMaintenances = useCallback(() => {
        setIsLoading(true);
        const listQuery = buildPrinterMaintenanceListQuery({
            page: currentPage,
            limit: itemsPerPage,
            printerUuid: queryFilters.printer_uuid,
            type: queryFilters.type,
            occurredFrom: queryFilters.occurred_from,
            occurredTo: queryFilters.occurred_to,
        });
        const summaryQuery = buildPrinterMaintenanceFilterQuery({
            printerUuid: queryFilters.printer_uuid,
            type: queryFilters.type,
            occurredFrom: queryFilters.occurred_from,
            occurredTo: queryFilters.occurred_to,
        });

        Promise.all([
            api.get(`printer-maintenances?${listQuery}`),
            api.get(`printer-maintenances/summary${summaryQuery ? `?${summaryQuery}` : ''}`),
        ])
            .then(([listResponse, summaryResponse]) => {
                const data = listResponse.data.data || {};
                setMaintenances(data.maintenances || []);
                setTotalPages(data.total_pages || 1);
                setSummaryPayload(summaryResponse.data.data || null);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, queryFilters.printer_uuid, queryFilters.type, queryFilters.occurred_from, queryFilters.occurred_to]);

    const summaryCards = useMemo(
        () => buildMaintenanceSummaryCardsFromAggregates(printers, summaryPayload?.by_printer)
            .filter((card) => card.downtime_minutes > 0),
        [printers, summaryPayload],
    );

    const overviewStats = useMemo(
        () => buildMaintenanceOverviewStatsFromSummary(printers, summaryPayload),
        [printers, summaryPayload],
    );

    const overviewPeriodLabel = formatMaintenancePeriodLabel(queryFilters.occurred_from, queryFilters.occurred_to);

    const maxSummaryDowntime = useMemo(
        () => summaryCards.reduce((max, card) => Math.max(max, card.downtime_minutes), 0),
        [summaryCards],
    );

    const handleSummaryPrinterSelect = (printerUuid) => {
        setDraftPrinter(printerUuid);
        setQueryFilters({
            ...queryFilters,
            printer_uuid: printerUuid,
        });
        triggerReload();
    };

    useListFetch(fetchMaintenances, [
        currentPage,
        itemsPerPage,
        listVersion,
        queryFilters.printer_uuid,
        queryFilters.type,
        queryFilters.occurred_from,
        queryFilters.occurred_to,
        fetchMaintenances,
    ]);

    const triggerReload = () => {
        setCurrentPage(1);
        setListVersion((version) => version + 1);
    };

    const handleApplyFilters = () => {
        let from = draftOverviewFrom;
        let to = draftOverviewTo;
        if (from && to && from > to) {
            [from, to] = [to, from];
            setDraftOverviewFrom(from);
            setDraftOverviewTo(to);
        }
        setQueryFilters({
            printer_uuid: draftPrinter,
            type: draftType,
            occurred_from: from,
            occurred_to: to,
        });
        triggerReload();
    };

    const handleClearFilters = () => {
        setDraftPrinter('');
        setDraftType('');
        setDraftOverviewFrom('');
        setDraftOverviewTo('');
        setQueryFilters({ printer_uuid: '', type: '', occurred_from: '', occurred_to: '' });
        triggerReload();
    };

    const handleDelete = (maintenanceId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta manutenção será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? 'Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (result.isConfirmed && !loadingDelete) {
                setLoadingDelete(true);
                api.delete(`printer-maintenances/${maintenanceId}`)
                    .then(() => {
                        setMaintenances((prev) => prev.filter((maintenance) => maintenance.id !== maintenanceId));
                        Swal.fire('Removido!', 'A manutenção foi removida com sucesso.', 'success');
                    })
                    .catch(() => {
                        Swal.fire('Erro!', 'Houve um problema ao remover a manutenção.', 'error');
                    })
                    .finally(() => {
                        setLoadingDelete(false);
                    });
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Manutenções</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: 2,
            }}>
                <Typography variant="h4" gutterBottom>
                    Manutenções
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1, width: isMobile ? '100%' : 'auto' }}>
                    <PageTutorialButtons pageKey="printer_maintenances" fullWidth={isMobile} />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/admin/register-printer-maintenance')}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Manutenção
                    </Button>
                </Box>
            </Box>

            <UpcomingMaintenancesPanel reloadKey={listVersion} />

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Filtros
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-printer-label">Impressora</InputLabel>
                            <Select
                                labelId="filter-printer-label"
                                label="Impressora"
                                value={draftPrinter}
                                onChange={(event) => setDraftPrinter(event.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {printers.map((printer) => (
                                    <MenuItem key={printer.id} value={printer.id}>
                                        {printer.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-type-label">Tipo</InputLabel>
                            <Select
                                labelId="filter-type-label"
                                label="Tipo"
                                value={draftType}
                                onChange={(event) => setDraftType(event.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {MAINTENANCE_TYPES.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data inicial"
                            type="date"
                            size="small"
                            fullWidth
                            value={draftOverviewFrom}
                            onChange={(event) => setDraftOverviewFrom(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: draftOverviewTo || undefined }}
                            helperText="Início do período"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data final"
                            type="date"
                            size="small"
                            fullWidth
                            value={draftOverviewTo}
                            onChange={(event) => setDraftOverviewTo(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: draftOverviewFrom || undefined }}
                            helperText="Fim do período"
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Button variant="contained" onClick={handleApplyFilters}>
                        Filtrar
                    </Button>
                    {hasActiveFilters && (
                        <Button variant="outlined" color="secondary" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                            Limpar filtros
                        </Button>
                    )}
                </Box>
            </Paper>

            {printers.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        Totais e paradas: {overviewPeriodLabel}
                    </Typography>
                    <MaintenanceOverviewCards stats={overviewStats} />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'stretch' : 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            mb: 0.5,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={600}>
                            Paradas por impressora
                        </Typography>
                        <ViewModeToggle
                            value={summaryViewMode}
                            onChange={setSummaryViewMode}
                            storageKey={MAINTENANCES_PRINTER_SUMMARY_VIEW_KEY}
                        />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        Ordenado pelo tempo total de interrupção (análise + correção), não pela quantidade de manutenções.
                    </Typography>
                    {summaryCards.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Nenhuma manutenção encontrada com os filtros atuais.
                        </Typography>
                    ) : summaryViewMode === 'list' ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Impressora</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Manutenções</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Tempo parado</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Reposições</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {summaryCards.map((printerCard) => {
                                        const brandBadge = getPrinterBrandBadgeProps(printerCard.brand);
                                        const partsCount = Number(printerCard.parts_count) || 0;
                                        const partsTotalCents = Number(printerCard.parts_total_cents) || 0;
                                        return (
                                            <TableRow
                                                key={printerCard.printer_uuid}
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSummaryPrinterSelect(printerCard.printer_uuid)}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                        {brandBadge ? (
                                                            <Chip
                                                                label={printerCard.name}
                                                                size="small"
                                                                {...brandBadge}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" fontWeight={700}>
                                                                {printerCard.name}
                                                            </Typography>
                                                        )}
                                                        <Box onClick={(event) => event.stopPropagation()}>
                                                            <PrinterFilamentSlots
                                                                printerUuid={printerCard.printer_uuid}
                                                                printerName={printerCard.name}
                                                                filaments={printerCard.filaments}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {Number(printerCard.maintenance_count) || 0}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700} color="primary.main">
                                                        {printerCard.downtime_minutes > 0
                                                            ? formatMaintenanceDurationLiteral(printerCard.downtime_minutes)
                                                            : '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {replacementPartsCountLabel(partsCount)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {partsTotalCents > 0
                                                            ? formatCurrencyInCents(partsTotalCents)
                                                            : 'R$ 0,00'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Grid container spacing={2}>
                            {summaryCards.map((printerCard) => {
                                const downtimePercent = maxSummaryDowntime > 0
                                    ? (printerCard.downtime_minutes / maxSummaryDowntime) * 100
                                    : 0;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={printerCard.printer_uuid}>
                                        <MaintenancePrinterSummaryCard
                                            printer={printerCard}
                                            downtimePercent={downtimePercent}
                                            onSelect={handleSummaryPrinterSelect}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Impressora</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Tempo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Contagem de horas</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Peças</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(maintenances) && maintenances.length > 0 && maintenances.map((maintenance) => {
                            const partsTotalCents = maintenancePartsTotalCents(maintenance.replacement_parts);
                            return (
                            <TableRow key={maintenance.id}>
                                <TableCell>{formatDate(maintenance.occurred_at)}</TableCell>
                                <TableCell>{printersById[maintenance.printer_uuid] || presentUuid(maintenance.printer_uuid)}</TableCell>
                                <TableCell>
                                    <Chip {...getMaintenanceTypeBadgeProps(maintenance.type)} />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{maintenance.problem_description}</Typography>
                                    {maintenance.performed_actions && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Feito: {maintenance.performed_actions}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        Análise: {minutesToDuration(maintenance.analysis_duration_minutes)}
                                    </Typography>
                                    <Typography variant="body2">
                                        Correção: {minutesToDuration(maintenance.correction_duration_minutes)}
                                    </Typography>
                                </TableCell>
                                <TableCell>{maintenance.printer_hours_count}h</TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {formatReplacementPartNames(maintenance.replacement_parts)}
                                    </Typography>
                                    {partsTotalCents > 0 && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Valor gasto: {formatCurrencyInCents(partsTotalCents)}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <IconButton color="primary" onClick={() => navigate(`/admin/edit-printer-maintenance/${maintenance.id}`)}>
                                        <Edit />
                                    </IconButton>
                                    <IconButton color="error" onClick={() => handleDelete(maintenance.id)} aria-label="remover manutenção">
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 3 }}>
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        setItemsPerPage(event.target.value);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default ListPrinterMaintenancesPage;
